// i18n
import { translateInComponent } from 'infras/i18n';
import translations from '../i18n.json';
import * as tracker from 'presentations/views/helpers/analytics';

import { useState, SyntheticEvent, useEffect, useContext } from 'react';
import { useRouter } from 'next/router';

import { destroy, get, patch } from 'infras/rest_client';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  apiPath,
  hostWebhookEditPath,
  hostWebhookNewPath,
} from 'presentations/routes/path';
import { SubscriptionStateItem } from 'presentations/api/schema/subscription';
import { useChat } from 'react-live-chat-loader';
import { useRequest } from 'presentations/views/hooks/request';
import { WebhookOptionIndex } from 'presentations/views/components/organisms/WebhookList';
import {
  DashboardPortalContext,
  setErrorMessage,
  setSuccessMessage,
} from 'presentations/reducers/DashboardPortal';
import {
  WebhookUserListItem,
  WebhookEndpointUpdateBody,
} from 'presentations/api/schema/webhook_endpoint';
import { WebhookEndpointStatus } from 'domains/models/webhook_endpoint/webhook_endpoint_status';
import { PlanId } from 'domains/models/subscription/planId';

interface Props {
  viewer: ViewerItem;
}
const perPage = 20;

export function useWebhookListPage(props: Props) {
  const t = translateInComponent(translations);

  const { state: navigationState, dispatch: navigationDispatch } = useContext(
    DashboardPortalContext,
  );

  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [
    deleteWebhook,
    setDeleteWebhook,
  ] = useState<WebhookUserListItem | null>(null);
  const [anchor, setAnchor] = useState<any>(null);
  const [isAccountLoading, setIsAccountLoading] = useState<boolean>(false);
  const [isShowingPlanUpgrade, setIsShowingPlanUpgrade] = useState<boolean>(
    false,
  );

  const router = useRouter();
  const [state, loadChat] = useChat();

  useEffect(() => {
    loadChat({ open: false });
    window.Intercom &&
      window.Intercom('update', { hide_default_launcher: false });

    return function () {
      window.Intercom &&
        window.Intercom('update', { hide_default_launcher: true });
    };
  }, []);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    if (router.query.created) {
      navigationDispatch(setSuccessMessage(t('Add webhook')));
    } else if (router.query.updated) {
      navigationDispatch(setSuccessMessage(t('Update webhook')));
    }
  }, [router.isReady, router.query.created, router.query.updated]);

  const webhookEndpointsRequest = useRequest(
    apiPath(`/v1/teams/${props.viewer.team!.uid}/webhook_endpoints`, {
      page: currentPage,
      perPage,
    }),
  );
  const webhookEndpoints: WebhookUserListItem[] =
    webhookEndpointsRequest.data || [];

  const subscriptionStateRequest = useRequest(
    apiPath(`/v1/teams/${props.viewer.team!.uid}/subscriptions/state`),
  );
  const subscriptionState: SubscriptionStateItem | null =
    subscriptionStateRequest.data;

  const isTeam = subscriptionState && subscriptionState.planId >= PlanId.Team;

  const onChangePage = async (e: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const onDeleteWebhook = async () => {
    if (!deleteWebhook) return;
    setIsConfirmLoading(true);
    const result = await destroy(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/webhook_endpoints/${
          deleteWebhook.id
        }/delete`,
      ),
    );
    if (!result.isSuccess) {
      setDeleteWebhook(null);
      setIsConfirmLoading(false);
      navigationDispatch(setErrorMessage(t('Failed Delete webhook')));
      return;
    }

    const newWebhookEndpoints = webhookEndpoints.filter(
      (e) => e.id !== deleteWebhook.id,
    );
    webhookEndpointsRequest.mutate(
      { ...webhookEndpointsRequest, data: newWebhookEndpoints },
      false,
    );
    setDeleteWebhook(null);
    setIsConfirmLoading(false);
    navigationDispatch(setSuccessMessage(t('Delete webhook')));
    tracker.setEvent('webhook delete');
  };

  const onClickNewWebhook = async (e: SyntheticEvent<HTMLButtonElement>) => {
    if (!isTeam) {
      setIsShowingPlanUpgrade(true);
    } else {
      router.push(hostWebhookNewPath());
    }
  };

  const onCloseDialog = () => {
    setDeleteWebhook(null);
  };

  const onSwitchActivation = async (webhook: WebhookUserListItem) => {
    const newStatus =
      webhook.status === WebhookEndpointStatus.Enable
        ? WebhookEndpointStatus.Disable
        : WebhookEndpointStatus.Enable;
    const data: WebhookEndpointUpdateBody = {
      status: newStatus,
    };

    if (!isTeam && newStatus === WebhookEndpointStatus.Enable) {
      setIsShowingPlanUpgrade(true);
      return;
    }

    const result = await patch(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/webhook_endpoints/${
          webhook.uid
        }/update`,
      ),
      { data },
    );

    if (!result.isSuccess) {
      navigationDispatch(setErrorMessage(t('Failed Save webhook')));
      return;
    }
    const newWebhookEndpoints = webhookEndpoints.map((w) =>
      w.id === webhook.id
        ? {
            ...w,
            status: newStatus,
          }
        : w,
    );
    webhookEndpointsRequest.mutate(
      {
        ...webhookEndpointsRequest,
        data: newWebhookEndpoints,
      },
      false,
    );
  };

  const onRollSecret = async (webhook: WebhookUserListItem) => {
    const result = await patch(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/webhook_endpoints/${
          webhook.uid
        }/roll_secret`,
      ),
    );

    if (!result.isSuccess) {
      navigationDispatch(setErrorMessage(t('Failed Save webhook')));
      return;
    }
    const newWebhookEndpoints = webhookEndpoints.map((w) =>
      w.id === webhook.id
        ? {
            ...w,
            secretKey: null,
          }
        : w,
    );
    webhookEndpointsRequest.mutate(
      {
        ...webhookEndpointsRequest,
        data: newWebhookEndpoints,
      },
      false,
    );
  };

  const onClickSecret = async (e: any, webhook: WebhookUserListItem) => {
    e.preventDefault();
    const result = await get(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/webhook_endpoints/${
          webhook.uid
        }/secret`,
      ),
    );
    if (result.isSuccess) {
      // setSecretKey(result.data.secretKey);
      const newWebhookEndpoints = webhookEndpoints.map((w) =>
        w.id === webhook.id
          ? {
              ...w,
              secretKey: result.data.secretKey,
            }
          : w,
      );
      webhookEndpointsRequest.mutate(
        {
          ...webhookEndpointsRequest,
          data: newWebhookEndpoints,
        },
        false,
      );
    }
  };

  const onSelectMenu = async (
    webhook: WebhookUserListItem,
    selectedIndex: number,
  ) => {
    switch (selectedIndex) {
      case WebhookOptionIndex.edit: {
        router.push(hostWebhookEditPath(webhook.uid));
        break;
      }
      case WebhookOptionIndex.activation: {
        onSwitchActivation(webhook);
        break;
      }
      case WebhookOptionIndex.roll: {
        onRollSecret(webhook);
        break;
      }
      case WebhookOptionIndex.delete: {
        setDeleteWebhook(webhook);
        break;
      }
      default:
        break;
    }
  };

  const onClosePlanUpgrade = () => {
    setIsShowingPlanUpgrade(false);
  };

  return {
    state: {
      webhookEndpoints,
      isAccountLoading,
      isLoading: webhookEndpointsRequest.isLoading,
      currentPage,
      numberOfPages: webhookEndpointsRequest.numberOfPages,
      isConfirmLoading,
      deleteWebhook,
      anchor,
      isShowingPlanUpgrade,
    },
    actions: {
      onChangePage,
      onSelectMenu,
      onDeleteWebhook,
      onCloseDialog,
      onClickNewWebhook,
      onRollSecret,
      onClickSecret,
      onClosePlanUpgrade,
    },
  };
}
